import type { ReactNode } from 'react';

import { colors } from '@packages/core/styles';
import { Button, type ButtonVariant, Icon, LoadingCircle, type StsIconName, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    button: {
        display: 'flex',
    },
    card: {
        alignItems: 'center',
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        minHeight: 80,
        minWidth: 200,
        overflow: 'auto',
        padding: '12px 16px',
    },
    content: {
        alignItems: 'center',
        display: 'flex',
        flex: '1 1 auto',
        gap: 16,
    },
    details: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 8,
    },
});

type DiagnosticSummaryCardProps = {
    /** The button's label. The button shows only with a label and `onButtonPress`. */
    buttonLabel?: string;

    /** The button's style. */
    buttonVariant?: ButtonVariant;

    /** Rows that aren't plain text, under the title and the description. */
    children?: ReactNode;

    /** The text under the title. */
    description?: string;

    /** The icon beside the text. */
    iconName?: StsIconName;

    /** Whether the button is disabled. */
    isButtonDisabled?: boolean;

    /** Whether to show a spinner in place of the icon. */
    isLoading?: boolean;

    /** Callback for the button. */
    onButtonPress?: () => void;

    /** The card's title, above the description. */
    title?: string;
};

/**
 * The card that shows where the report's CVT diagnostic stands.
 *
 * Renders an icon, or a spinner in its place, a title, a description and `children`, with a button when given a label
 * and a press handler.
 */
export function DiagnosticSummaryCard({
    buttonLabel,
    buttonVariant,
    children,
    description,
    iconName,
    isButtonDisabled,
    isLoading,
    onButtonPress,
    title,
}: DiagnosticSummaryCardProps) {
    const classes = useStyles();

    const showButton = Boolean(buttonLabel) && Boolean(onButtonPress);

    return (
        <div className={classes.card}>
            <div className={classes.content}>
                {isLoading && <LoadingCircle size={22} />}
                {!isLoading && iconName && <Icon color="textDarkSecondary" name={iconName} size={22} />}
                <div className={classes.details}>
                    {title && (
                        <Typography color="textDarkSecondary" variant="label">
                            {title}
                        </Typography>
                    )}
                    {description && (
                        <Typography color="textDarkSecondary" variant="caption">
                            {description}
                        </Typography>
                    )}
                    {children}
                </div>
            </div>
            {showButton && (
                <div className={classes.button}>
                    <Button disabled={isButtonDisabled} onPress={onButtonPress} variant={buttonVariant}>
                        {buttonLabel}
                    </Button>
                </div>
            )}
        </div>
    );
}
